import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import rpLogo from '../../ReactivePixels_light-text.png';
import puttwiseLogo from '../../assets/puttwise_graphic_green.png';
import appstoreDownload from '../../assets/appstore-download.svg';
import googleplayDownload from '../../assets/googleplay-download.svg';
import screenshotPlayerHome from '../../assets/puttwise-screenshots/player-home.png';
import screenshotPlayerNewRound from '../../assets/puttwise-screenshots/player-new-round.png';
import screenshotPlayerScorecard from '../../assets/puttwise-screenshots/player-scorecard.png';
import screenshotPlayerStatistics from '../../assets/puttwise-screenshots/player-statistics.png';
import screenshotPlayerResultsHighlights from '../../assets/puttwise-screenshots/player-results-highlights-blur.png';
import screenshotPlayerResultsMatchplay from '../../assets/puttwise-screenshots/player-results-matchplay.png';
import screenshotPlayerPreviousRounds from '../../assets/puttwise-screenshots/player-previous-rounds.png';
import screenshotCourseScorecard from '../../assets/puttwise-screenshots/course-scorecard-offer.png';
import screenshotCourseResults from '../../assets/puttwise-screenshots/course-results-offer.png';
import screenshotCourseInfobox from '../../assets/puttwise-screenshots/course-infobox.png';
import screenshotCourseHoleInfo from '../../assets/puttwise-screenshots/course-hole-info.png';
import { Lang, LANGUAGES, Translations, translations } from './translations';

type Audience = 'player' | 'venue';

const DEFAULT_LANG: Lang = 'sv';

function useLang(): [Lang, (l: Lang) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('lang') as Lang | null;
  const lang: Lang = raw && translations[raw] ? raw : DEFAULT_LANG;
  const setLang = (l: Lang) => {
    setSearchParams(prev => { prev.set('lang', l); return prev; }, { replace: true });
  };
  return [lang, setLang];
}

/* ─── Language switcher ──────────────────────────────────────────────────── */

const LanguageSwitcher = ({ lang, setLang }: { lang: Lang; setLang: (l: Lang) => void }) => (
  <LangSelect value={lang} onChange={e => setLang(e.target.value as Lang)}>
    {LANGUAGES.map(l => (
      <option key={l.code} value={l.code}>{l.label}</option>
    ))}
  </LangSelect>
);

/* ─── Main component ─────────────────────────────────────────────────────── */

function useAudience(): [Audience, (a: Audience) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('type');
  const audience: Audience = raw === 'course' ? 'venue' : 'player';
  const setAudience = (a: Audience) => {
    setSearchParams(prev => { prev.set('type', a === 'venue' ? 'course' : 'player'); return prev; }, { replace: true });
  };
  return [audience, setAudience];
}

const Puttwise = () => {
  const [audience, setAudience] = useAudience();
  const [lang, setLang] = useLang();
  const t = translations[lang];

  return (
    <>
      <GlobalStyle />
      <Page>
        <TopBar>
          <RPLogo href="/" title="Reactive Pixels">
            <img src={rpLogo} alt="Reactive Pixels" />
          </RPLogo>
          <PuttwiseBrand>
            <BrandLogoImg src={puttwiseLogo} alt="Puttwise" />
            <BrandName>Puttwise</BrandName>
          </PuttwiseBrand>
          <TopBarRight>
            <LanguageSwitcher lang={lang} setLang={setLang} />
          </TopBarRight>
        </TopBar>

        <ToggleSection>
          <TogglePill>
            <ToggleOption $active={audience === 'player'} onClick={() => setAudience('player')}>
              {t.togglePlayer}
            </ToggleOption>
            <ToggleOption $active={audience === 'venue'} onClick={() => setAudience('venue')}>
              {t.toggleVenue}
            </ToggleOption>
          </TogglePill>
        </ToggleSection>

        <ContentArea>
          {audience === 'player' ? (
            <PlayerContent t={t} lang={lang} />
          ) : (
            <VenueContent t={t} onSwitchToPlayer={() => { setAudience('player'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
          )}
        </ContentArea>
      </Page>
    </>
  );
};

/* ─── Lightbox ───────────────────────────────────────────────────────────── */

type ScreenshotMeta = {
  label: string;
  gradient: string;
  lines: string[];
};

type LightboxProps = {
  screenshot: ScreenshotMeta;
  onClose: () => void;
};

const Lightbox = ({ screenshot, onClose }: LightboxProps) => (
  <LightboxOverlay onClick={onClose}>
    <LightboxInner onClick={e => e.stopPropagation()}>
      <LightboxClose onClick={onClose}>✕</LightboxClose>
      <PhoneMockup large gradient={screenshot.gradient}>
        <PhoneStatusBar />
        <PhoneScreen>
          {screenshot.lines.map((line, i) => (
            <MockLine key={i} $bold={i === 0} $dim={i > 0}>{line}</MockLine>
          ))}
        </PhoneScreen>
        <PhoneHomeBar />
      </PhoneMockup>
      <LightboxLabel>{screenshot.label}</LightboxLabel>
    </LightboxInner>
  </LightboxOverlay>
);

/* ─── Phone thumbnail ────────────────────────────────────────────────────── */

const PhoneThumbnail = ({ screenshot, hint, onClick }: { screenshot: ScreenshotMeta; hint: string; onClick: () => void }) => (
  <ThumbnailWrapper onClick={onClick} title={hint}>
    <PhoneMockup large={false} gradient={screenshot.gradient}>
      <PhoneStatusBar />
      <PhoneScreen small>
        {screenshot.lines.slice(0, 3).map((line, i) => (
          <MockLine key={i} $bold={i === 0} $dim={i > 0} $small>{line}</MockLine>
        ))}
      </PhoneScreen>
      <PhoneHomeBar />
    </PhoneMockup>
    <ThumbnailHint>{hint}</ThumbnailHint>
  </ThumbnailWrapper>
);

/* ─── Player content ─────────────────────────────────────────────────────── */

const AppPhone = ({ lines, gradient }: { lines: string[]; gradient: string }) => (
  <AppPhoneFrame>
    <AppPhoneStatusBar />
    <AppPhoneTitle>{lines[0]}</AppPhoneTitle>
    <AppPhoneBody gradient={gradient}>
      {lines.slice(1).map((line, i) => (
        <AppPhoneLine key={i} $bold={i === 0 || line.startsWith('🥇') || line.startsWith('🥈') || line.startsWith('🥉')} $dim={line.startsWith('⸻')}>
          {line}
        </AppPhoneLine>
      ))}
    </AppPhoneBody>
    <AppPhoneHomeBar />
  </AppPhoneFrame>
);

const playerScreenshots = [
  screenshotPlayerHome,
  screenshotPlayerNewRound,
  screenshotPlayerScorecard,
];

const appStoreLocale: Record<Lang, string> = { sv: 'se', no: 'no', da: 'dk', en: 'se', de: 'de' };

const PlayerContent = ({ t, lang }: { t: Translations; lang: Lang }) => {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  const premiumFeatures = [
    { icon: '⭐', title: t.playerFeat3Title, desc: t.playerFeat3Desc, lines: t.playerMockMatchLines,   gradient: 'linear-gradient(160deg, #2a1a40 0%, #180d28 100%)', screenshots: [screenshotPlayerResultsHighlights, screenshotPlayerResultsMatchplay] },
    { icon: '📊', title: t.playerFeat4Title, desc: t.playerFeat4Desc, lines: t.playerMockStatsLines,   gradient: 'linear-gradient(160deg, #14381a 0%, #0a1e0d 100%)', screenshots: [screenshotPlayerStatistics] },
    { icon: '🕒', title: t.playerFeat5Title, desc: t.playerFeat5Desc, lines: t.playerMockHistoryLines, gradient: 'linear-gradient(160deg, #183018 0%, #0c1a0c 100%)', screenshots: [screenshotPlayerPreviousRounds] },
  ];

  return (
    <PlayerWrapper>
      {lightboxSrc && (
        <LightboxOverlay onClick={() => setLightboxSrc(null)}>
          <LightboxInner onClick={e => e.stopPropagation()}>
            <PlayerLightboxImg src={lightboxSrc} />
            <ReportCloseButton style={{ position: 'absolute', top: -16, right: -16 }} onClick={() => setLightboxSrc(null)}>✕</ReportCloseButton>
          </LightboxInner>
        </LightboxOverlay>
      )}

      <Section $noPaddingBottom>
        <SectionLabel>{t.playerSec1Label}</SectionLabel>
        <SectionHeading>{t.playerSec1Heading}</SectionHeading>
        <SectionBody>{t.playerSec1Body}</SectionBody>
      </Section>

      <AppDownloadRow>
        <a href={`https://apps.apple.com/${appStoreLocale[lang]}/app/puttwise/id6754455181`} target="_blank" rel="noopener noreferrer">
          <img src={appstoreDownload} alt="Ladda ner på App Store" style={{ height: '50px', display: 'block' }} />
        </a>
        <a href="https://play.google.com/store/apps/details?id=se.reactivepixels.puttwise" target="_blank" rel="noopener noreferrer">
          <img src={googleplayDownload} alt="Ladda ner på Google Play" style={{ height: '50px', display: 'block' }} />
        </a>
      </AppDownloadRow>

      <PlayerSectionDivider>
        <PlayerSectionBadge $free>{t.playerFreeSectionLabel}</PlayerSectionBadge>
      </PlayerSectionDivider>

      <PlayerFeatureList>
        <PlayerFeatureRow $reverse={false}>
          <PlayerFeatureText>
            <PlayerFeatureTitle><PlayerFeatureIcon>📋</PlayerFeatureIcon>{t.playerFeat1Title}</PlayerFeatureTitle>
            <PlayerFeatureDesc>{t.playerFeat1Desc}</PlayerFeatureDesc>
            <PlayerNoAccountNote>✓ {t.playerFeat1NoAccount}</PlayerNoAccountNote>
          </PlayerFeatureText>
          <PlayerScreenshotRow>
            {playerScreenshots.map((src, i) => (
              <PlayerPhoneThumb key={i} onClick={() => setLightboxSrc(src)}>
                <PlayerPhoneThumbImg src={src} />
              </PlayerPhoneThumb>
            ))}
          </PlayerScreenshotRow>
        </PlayerFeatureRow>

        <PlayerFeatureRow $reverse={true}>
          <PlayerFeatureText>
            <PlayerFeatureTitle><PlayerFeatureIcon>🏆</PlayerFeatureIcon>{t.playerFeat2Title}</PlayerFeatureTitle>
            <PlayerFeatureDesc>{t.playerFeat2Desc}</PlayerFeatureDesc>
          </PlayerFeatureText>
          <PlayerPhoneThumb onClick={() => setLightboxSrc(screenshotCourseResults)}><PlayerPhoneThumbImg src={screenshotCourseResults} /></PlayerPhoneThumb>
        </PlayerFeatureRow>
      </PlayerFeatureList>

      <PlayerSectionDivider>
        <PlayerSectionBadge>{t.playerPremiumSectionLabel}</PlayerSectionBadge>
      </PlayerSectionDivider>

      <PlayerPremiumIntro>
        <PlayerPremiumHeading>{t.playerPremiumSectionHeading}</PlayerPremiumHeading>
        <PlayerPremiumBody>{t.playerPremiumSectionBody}</PlayerPremiumBody>
      </PlayerPremiumIntro>

      <PlayerFeatureList>
        {premiumFeatures.map((f, i) => (
          <PlayerFeatureRow key={f.title} $reverse={i % 2 === 1}>
            <PlayerFeatureText>
              <PlayerFeatureTitle><PlayerFeatureIcon>{f.icon}</PlayerFeatureIcon>{f.title}</PlayerFeatureTitle>
              <PlayerFeatureDesc>{f.desc}</PlayerFeatureDesc>
            </PlayerFeatureText>
            {f.screenshots && f.screenshots.length > 0
              ? <PlayerScreenshotRow>
                  {f.screenshots.map((src, si) => (
                    <PlayerPhoneThumb key={si} onClick={() => setLightboxSrc(src)}>
                      <PlayerPhoneThumbImg src={src} />
                    </PlayerPhoneThumb>
                  ))}
                </PlayerScreenshotRow>
              : <AppPhone lines={f.lines} gradient={f.gradient} />
            }
          </PlayerFeatureRow>
        ))}
      </PlayerFeatureList>

    </PlayerWrapper>
  );
};

/* ─── Venue content ──────────────────────────────────────────────────────── */

const VenueContent = ({ t, onSwitchToPlayer }: { t: Translations; onSwitchToPlayer: () => void }) => {
  const [lightbox, setLightbox] = useState<ScreenshotMeta | null>(null);
  const [imgLightbox, setImgLightbox] = useState<string | null>(null);
  const [showReport, setShowReport] = useState(false);

  const screenshots = {
    partners: { label: t.screenshotPartnersLabel, gradient: 'linear-gradient(160deg, #1a3a2a 0%, #0d2218 100%)', lines: t.screenshotPartnersLines },
    infobox:  { label: t.screenshotInfoboxLabel,  gradient: 'linear-gradient(160deg, #1f2a1a 0%, #121a0d 100%)', lines: t.screenshotInfoboxLines },
    holeinfo: { label: t.screenshotHoleinfoLabel, gradient: 'linear-gradient(160deg, #1a2a3a 0%, #0d1828 100%)', lines: t.screenshotHoleinfoLines },
    offers:   { label: t.screenshotOffersLabel,   gradient: 'linear-gradient(160deg, #2a1a10 0%, #180d08 100%)', lines: t.screenshotOffersLines },
  };

  return (
    <VenueWrapper>
      {lightbox && <Lightbox screenshot={lightbox} onClose={() => setLightbox(null)} />}
      {imgLightbox && (
        <LightboxOverlay onClick={() => setImgLightbox(null)}>
          <LightboxInner onClick={e => e.stopPropagation()}>
            <PlayerLightboxImg src={imgLightbox} />
            <ReportCloseButton style={{ position: 'absolute', top: -16, right: -16 }} onClick={() => setImgLightbox(null)}>✕</ReportCloseButton>
          </LightboxInner>
        </LightboxOverlay>
      )}
      {showReport && (
        <LightboxOverlay onClick={() => setShowReport(false)}>
          <ReportModalInner onClick={e => e.stopPropagation()}>
            <ReportModalHeader>
              <ReportModalTitle>{t.reportModalTitle}</ReportModalTitle>
              <ReportCloseButton onClick={() => setShowReport(false)}>✕</ReportCloseButton>
            </ReportModalHeader>
            <ReportModalDescText>{t.reportModalDesc}</ReportModalDescText>
            <ReportSections>
              {t.reportSections.map(section => (
                <ReportSection key={section.title}>
                  <ReportSectionTitle>{section.title}</ReportSectionTitle>
                  <ReportList>
                    {section.items.map(item => (
                      <ReportListItem key={item}>{item}</ReportListItem>
                    ))}
                  </ReportList>
                </ReportSection>
              ))}
            </ReportSections>
          </ReportModalInner>
        </LightboxOverlay>
      )}

      {/* Section 1 — About */}
      <Section>
        <SectionLabel>{t.sec1Label}</SectionLabel>
        <SectionHeading>{t.sec1Heading}</SectionHeading>
        <SectionBody>{t.sec1Body}</SectionBody>
        <ValueGrid>
          <ValueCard><ValueCardHeader><ValueIcon>♻️</ValueIcon><ValueTitle>{t.val1Title}</ValueTitle></ValueCardHeader><ValueDesc>{t.val1Desc}</ValueDesc></ValueCard>
          <ValueCard><ValueCardHeader><ValueIcon>📊</ValueIcon><ValueTitle>{t.val2Title}</ValueTitle></ValueCardHeader><ValueDesc>{t.val2Desc}</ValueDesc></ValueCard>
          <ValueCard><ValueCardHeader><ValueIcon>⭐</ValueIcon><ValueTitle>{t.val3Title}</ValueTitle></ValueCardHeader><ValueDesc>{t.val3Desc}</ValueDesc></ValueCard>
          <ValueCard $wide><ValueCardHeader><ValueIcon>📣</ValueIcon><ValueTitle>{t.val4Title}</ValueTitle></ValueCardHeader><ValueDesc>{t.val4Desc}</ValueDesc></ValueCard>
          <ValueCard $wide><ValueCardHeader><ValueIcon>🌐</ValueIcon><ValueTitle>{t.val5Title}</ValueTitle></ValueCardHeader><ValueDesc>{t.val5Desc}</ValueDesc></ValueCard>
        </ValueGrid>
        <RecBox>
          <QrInfoIcon>💡</QrInfoIcon>
          <QrInfoText>
            <QrInfoTitle>{t.recTitle}</QrInfoTitle>
            <QrInfoDesc>{t.recBody}</QrInfoDesc>
          </QrInfoText>
        </RecBox>
      </Section>

      {/* Section 2 — Free */}
      <Section>
        <SectionLabel>{t.sec2Label}</SectionLabel>
        <SectionHeading>{t.sec2Heading}</SectionHeading>
        <SectionBody>{t.sec2Body}</SectionBody>
        <FeatureGrid>
          <FeatureItem>
            <FeatureItemTop>
              <FeatureIcon>🏅</FeatureIcon>
              <FeatureText>
                <FeatureTitleRow>
                  <FeatureTitle>{t.feat1Title}</FeatureTitle>
                  <PriceBadge $free>{t.freeBadge}</PriceBadge>
                </FeatureTitleRow>
                <FeatureDesc>{t.feat1Desc}</FeatureDesc>
              </FeatureText>
            </FeatureItemTop>
            <PlayerViewBtn onClick={onSwitchToPlayer}>{t.feat1PlayerViewBtn} →</PlayerViewBtn>
          </FeatureItem>
          <FeatureItem>
            <FeatureItemTop>
              <FeatureIcon>🤝</FeatureIcon>
              <FeatureText>
                <FeatureTitleRow>
                  <FeatureTitle>{t.feat2Title}</FeatureTitle>
                  <PriceBadge $free>{t.freeBadge}</PriceBadge>
                </FeatureTitleRow>
                <FeatureDesc>{t.feat2Desc}</FeatureDesc>
              </FeatureText>
            </FeatureItemTop>
            <VenueScreenshotRow>
              <PlayerPhoneThumb onClick={() => setImgLightbox(screenshotPlayerScorecard)}>
                <PlayerPhoneThumbImg src={screenshotPlayerScorecard} />
              </PlayerPhoneThumb>
            </VenueScreenshotRow>
          </FeatureItem>
          <FeatureItem>
            <FeatureItemTop>
              <FeatureIcon>ℹ️</FeatureIcon>
              <FeatureText>
                <FeatureTitleRow>
                  <FeatureTitle>{t.feat3Title}</FeatureTitle>
                  <PriceBadge $free>{t.freeBadge}</PriceBadge>
                </FeatureTitleRow>
                <FeatureDesc>{t.feat3Desc}</FeatureDesc>
              </FeatureText>
            </FeatureItemTop>
            <VenueScreenshotRow>
              <PlayerPhoneThumb onClick={() => setImgLightbox(screenshotCourseInfobox)}>
                <PlayerPhoneThumbImg src={screenshotCourseInfobox} />
              </PlayerPhoneThumb>
            </VenueScreenshotRow>
          </FeatureItem>
          <FeatureItem>
            <FeatureItemTop>
              <FeatureIcon>🗺️</FeatureIcon>
              <FeatureText>
                <FeatureTitleRow>
                  <FeatureTitle>{t.feat4Title}</FeatureTitle>
                  <PriceBadge $free>{t.freeBadge}</PriceBadge>
                </FeatureTitleRow>
                <FeatureDesc>{t.feat4Desc}</FeatureDesc>
              </FeatureText>
            </FeatureItemTop>
            <VenueScreenshotRow>
              <PlayerPhoneThumb onClick={() => setImgLightbox(screenshotCourseHoleInfo)}>
                <PlayerPhoneThumbImg src={screenshotCourseHoleInfo} />
              </PlayerPhoneThumb>
            </VenueScreenshotRow>
          </FeatureItem>
        </FeatureGrid>
        <QrInfoBox>
          <QrInfoIcon>📦</QrInfoIcon>
          <QrInfoText>
            <QrInfoTitle>{t.qrTitle}</QrInfoTitle>
            <QrInfoDesc>{t.qrDesc}</QrInfoDesc>
          </QrInfoText>
        </QrInfoBox>
        <ContactNote>{t.contact1}</ContactNote>
        <ContactNote>{t.contact2}</ContactNote>
        <ContactNote>
          {t.contactPrefix}{' '}
          <a href="mailto:info@reactivepixels.se">info@reactivepixels.se</a>
        </ContactNote>
      </Section>

      {/* Section 3 — Paid */}
      <Section>
        <SectionLabel>{t.sec3Label}</SectionLabel>
        <SectionHeading>{t.sec3Heading}</SectionHeading>
        <SectionBody>{t.sec3Body}</SectionBody>
        <FeatureGrid>
          <FeatureItem $paid>
            <FeatureItemTop>
              <FeatureIcon>🎁</FeatureIcon>
              <FeatureText>
                <FeatureTitleRow>
                  <FeatureTitle>{t.paid1Title}</FeatureTitle>
                  <PriceBadge>{t.paid1Price}</PriceBadge>
                </FeatureTitleRow>
                <FeatureDesc>{t.paid1Desc}</FeatureDesc>
              </FeatureText>
            </FeatureItemTop>
            <VenueScreenshotRow>
              <PlayerPhoneThumb onClick={() => setImgLightbox(screenshotCourseScorecard)}>
                <PlayerPhoneThumbImg src={screenshotCourseScorecard} />
              </PlayerPhoneThumb>
              <PlayerPhoneThumb onClick={() => setImgLightbox(screenshotCourseResults)}>
                <PlayerPhoneThumbImg src={screenshotCourseResults} />
              </PlayerPhoneThumb>
            </VenueScreenshotRow>
          </FeatureItem>
          <FeatureItem $paid>
            <FeatureItemTop>
              <FeatureIcon>📈</FeatureIcon>
              <FeatureText>
                <FeatureTitleRow>
                  <FeatureTitle>{t.paid2Title}</FeatureTitle>
                  <PriceBadge>{t.paid2Price}</PriceBadge>
                </FeatureTitleRow>
                <FeatureDesc>{t.paid2Desc}</FeatureDesc>
                <ReportDetailsButton onClick={() => setShowReport(true)}>
                  {t.reportButton} →
                </ReportDetailsButton>
              </FeatureText>
            </FeatureItemTop>
          </FeatureItem>
        </FeatureGrid>
        <ContactNote>
          {t.contact3}{' '}
          <a href="mailto:info@reactivepixels.se">info@reactivepixels.se</a>
        </ContactNote>
      </Section>

    </VenueWrapper>
  );
};

/* ─── Global & page styles ───────────────────────────────────────────────── */

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@500;700;800&family=Hind:wght@400;600&display=swap');
  body {
    margin: 0;
    padding: 0;
    background: #0b1c0e;
    font-family: 'Hind', system-ui, sans-serif;
    color: #e8f5e8;
    min-height: 100vh;
  }
  h1, h2, h3, h4 {
    letter-spacing: 0;
  }
`;

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: radial-gradient(ellipse at 50% 0%, #1a3d1f 0%, #0b1c0e 60%);
`;

const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 32px 24px;
  position: relative;
`;

const RPLogo = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;
  img {
    height: 40px;
    opacity: 0.7;
    transition: opacity 0.2s;
    @media (max-width: 767px) { height: 24px; }
  }
  &:hover img { opacity: 1; }
`;

const PuttwiseBrand = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BrandLogoImg = styled.img`
  height: 108px;
  @media (max-width: 767px) { height: 54px; }
`;

const BrandName = styled.span`
  font-size: 64px;
  font-weight: 800;
  letter-spacing: 0px;
  color: #ffffff;
  font-family: 'Montserrat', system-ui, sans-serif;
  @media (max-width: 767px) { font-size: 32px; }
`;

const TopBarRight = styled.div`
  display: flex;
  justify-content: flex-end;
  min-width: 120px;
`;

const LangSelect = styled.select`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.65);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
  outline: none;
  appearance: auto;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
  }
  option {
    background: #122018;
    color: #fff;
  }
`;

const ToggleSection = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
  @media (max-width: 767px) { margin-top: 4px; margin-bottom: -16px; }
`;

const TogglePill = styled.div`
  display: flex;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 100px;
  padding: 4px;
  gap: 4px;
`;

const ToggleOption = styled.button<{ $active: boolean }>`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 15px;
  font-weight: ${({ $active }) => ($active ? '700' : '400')};
  padding: 10px 32px;
  border: none;
  border-radius: 100px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  background: ${({ $active }) => $active ? '#3aad5a' : 'transparent'};
  color: ${({ $active }) => $active ? '#ffffff' : 'rgba(255,255,255,0.55)'};
  &:hover {
    color: #ffffff;
    background: ${({ $active }) => $active ? '#3aad5a' : 'rgba(255,255,255,0.1)'};
  }
`;

const ContentArea = styled.main`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 24px;
  @media (max-width: 767px) { padding-top: 4px; }
`;


/* ─── Venue styles ───────────────────────────────────────────────────────── */

const VenueWrapper = styled.div`
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  padding: 20px 0 80px;
`;

const Section = styled.section<{ $noPaddingBottom?: boolean }>`
  padding: 60px 0 20px;
  &:first-child { padding-top: 30px; }
  ${({ $noPaddingBottom }) => $noPaddingBottom && 'padding-bottom: 0;'}
`;

const SectionLabel = styled.div`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #3aad5a;
  margin-bottom: 10px;
`;

const SectionHeading = styled.h2`
  font-family: 'Montserrat', system-ui, sans-serif;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 0px;
  color: #ffffff;
  margin: 0 0 14px;
  line-height: 1.25;
`;

const SectionBody = styled.p`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 16px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.65);
  margin: 0 0 28px;
`;

const HighlightBox = styled.div`
  display: flex;
  gap: 18px;
  background: rgba(58, 173, 90, 0.08);
  border: 1px solid rgba(58, 173, 90, 0.3);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  @media (max-width: 767px) { padding: 14px; }
`;

const HighlightIcon = styled.div`
  font-size: 28px;
  flex-shrink: 0;
  margin-top: 2px;
`;

const HighlightText = styled.div`
  flex: 1;
`;

const HighlightTitle = styled.div`
  font-family: 'Montserrat', system-ui, sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 6px;
`;

const HighlightDesc = styled.div`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.6);
`;

const ValueGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const ValueCard = styled.div<{ $wide?: boolean }>`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 20px 16px;
  grid-column: ${({ $wide }) => $wide ? 'span 3' : 'span 2'};
  @media (max-width: 600px) { grid-column: span 1; }
  @media (max-width: 767px) { padding: 14px 12px; }
`;

const ValueCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const ValueIcon = styled.div`
  font-size: 20px;
  flex-shrink: 0;
`;

const ValueTitle = styled.div`
  font-family: 'Montserrat', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
`;

const ValueDesc = styled.div`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.5);
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 20px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const FeatureItem = styled.div<{ $paid?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid ${({ $paid }) => $paid ? 'rgba(58, 173, 90, 0.25)' : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 12px;
  padding: 20px;
  @media (max-width: 767px) { padding: 14px; }
`;

const FeatureItemTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const FeatureIcon = styled.div`
  font-size: 24px;
  flex-shrink: 0;
  margin-top: 2px;
`;

const FeatureText = styled.div`
  flex: 1;
`;

const FeatureTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
`;

const FeatureTitle = styled.div`
  font-family: 'Montserrat', system-ui, sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
`;

const FeatureDesc = styled.div`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.55);
`;

const PriceBadge = styled.span<{ $free?: boolean }>`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $free }) => $free ? 'rgba(58, 173, 90, 0.15)' : 'rgba(255, 255, 255, 0.08)'};
  color: ${({ $free }) => $free ? '#3aad5a' : 'rgba(255,255,255,0.6)'};
  border: 1px solid ${({ $free }) => $free ? 'rgba(58, 173, 90, 0.35)' : 'rgba(255,255,255,0.15)'};
  border-radius: 100px;
  padding: 2px 10px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const PlayerViewBtn = styled.button`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #3aad5a;
  background: transparent;
  border: 1px solid rgba(58, 173, 90, 0.35);
  border-radius: 100px;
  padding: 5px 14px;
  margin-top: 10px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: rgba(58, 173, 90, 0.12);
  }
`;

const ContactNote = styled.p`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.35);
  margin: 0 0 8px;
  a {
    color: #3aad5a;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;

const QrInfoBox = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 16px;
  @media (max-width: 767px) { padding: 14px; }
`;

const RecBox = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  background: rgba(58, 173, 90, 0.06);
  border: 1px solid rgba(58, 173, 90, 0.2);
  border-left: 3px solid rgba(58, 173, 90, 0.6);
  border-radius: 12px;
  padding: 18px 20px;
  margin-top: 24px;
  @media (max-width: 767px) { padding: 14px; }
`;

const QrInfoIcon = styled.div`
  font-size: 24px;
  flex-shrink: 0;
  margin-top: 2px;
`;

const QrInfoText = styled.div`
  flex: 1;
`;

const QrInfoTitle = styled.div`
  font-family: 'Montserrat', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 6px;
`;

const QrInfoDesc = styled.div`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.5);
`;

/* ─── Player section styles ──────────────────────────────────────────────── */

const PlayerWrapper = styled.div`
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  padding: 20px 0 80px;
`;

const PlayerSectionDivider = styled.div`
  display: flex;
  align-items: center;
  margin: 8px 0 32px;
  gap: 12px;
  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
  }
  @media (max-width: 767px) { margin-top: 24px; }
`;

const PlayerSectionBadge = styled.span<{ $free?: boolean }>`
  font-family: 'Montserrat', system-ui, sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: 20px;
  background: ${({ $free }) => $free ? 'rgba(80, 180, 100, 0.18)' : 'rgba(180, 130, 255, 0.18)'};
  color: ${({ $free }) => $free ? '#6dcc88' : '#c49fff'};
  border: 1px solid ${({ $free }) => $free ? 'rgba(80, 180, 100, 0.3)' : 'rgba(180, 130, 255, 0.3)'};
  white-space: nowrap;
`;

const PlayerPremiumIntro = styled.div`
  margin-bottom: 40px;
`;

const PlayerPremiumHeading = styled.h3`
  font-family: 'Montserrat', system-ui, sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 10px;
`;

const PlayerPremiumBody = styled.p`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 15px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
`;

const PlayerFeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;
  margin-bottom: 48px;
`;

const PlayerFeatureRow = styled.div<{ $reverse: boolean }>`
  display: flex;
  align-items: center;
  gap: 40px;
  flex-direction: ${({ $reverse }) => $reverse ? 'row-reverse' : 'row'};
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: center;
  }
`;

const PlayerFeatureText = styled.div`
  flex: 1;
`;

const PlayerFeatureIcon = styled.span`
  font-size: 22px;
  margin-right: 10px;
  vertical-align: middle;
  line-height: 1;
`;

const PlayerFeatureTitle = styled.div`
  font-family: 'Montserrat', system-ui, sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
`;

const PlayerFeatureDesc = styled.div`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 15px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.6);
`;

const PlayerNoAccountNote = styled.div`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 13px;
  color: rgba(58, 173, 90, 0.85);
  margin-top: 10px;
  line-height: 1.5;
`;

const PlayerScreenshotRow = styled.div`
  display: flex;
  gap: 10px;
  flex-shrink: 0;
  @media (max-width: 767px) { margin-top: -8px; justify-content: center; width: 100%; }
`;

const VenueScreenshotRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  width: 100%;
`;

const PlayerPhoneThumb = styled.div`
  width: 90px;
  border-radius: 14px;
  overflow: hidden;
  border: 1.5px solid rgba(255, 255, 255, 0.12);
  cursor: pointer;
  align-self: center;
  transition: transform 0.15s, border-color 0.15s;
  &:hover {
    transform: scale(1.04);
    border-color: rgba(58, 173, 90, 0.5);
  }
  @media (max-width: 767px) {
    width: 68px;
  }
`;

const PlayerPhoneThumbImg = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

const PlayerLightboxImg = styled.img`
  max-height: 85vh;
  max-width: min(400px, 90vw);
  border-radius: 20px;
  display: block;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.7);
`;

const PlayerCta = styled.div`
  background: rgba(58, 173, 90, 0.08);
  border: 1px solid rgba(58, 173, 90, 0.25);
  border-radius: 16px;
  padding: 32px;
  text-align: center;
`;

const PlayerCtaHeading = styled.h3`
  font-family: 'Montserrat', system-ui, sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 12px;
`;

const PlayerCtaBody = styled.p`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 15px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.55);
  margin: 0 0 24px;
`;

const PlayerCtaButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const PlayerCtaBtn = styled.a`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #3aad5a;
  color: #ffffff;
  text-decoration: none;
  padding: 12px 24px;
  border-radius: 100px;
  transition: background 0.2s;
  &:hover { background: #2e9649; }
`;

const AppDownloadRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin: -8px 0 8px 0;
`;

const AppDownloadBtn = styled.a`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #3aad5a;
  color: #ffffff;
  text-decoration: none;
  padding: 9px 18px;
  border-radius: 100px;
  transition: background 0.2s;
  &:hover { background: #2e9649; }
`;

/* ─── App phone mockup styles ────────────────────────────────────────────── */

const AppPhoneFrame = styled.div`
  width: 180px;
  min-height: 340px;
  flex-shrink: 0;
  background: #0d2210;
  border: 2px solid rgba(255,255,255,0.12);
  border-radius: 28px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0,0,0,0.5);
  @media (max-width: 600px) {
    width: 100%;
    min-height: auto;
    border-radius: 16px;
  }
`;

const AppPhoneStatusBar = styled.div`
  height: 10px;
  background: rgba(0,0,0,0.3);
  flex-shrink: 0;
`;

const AppPhoneTitle = styled.div`
  font-family: 'Montserrat', system-ui, sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #f0c040;
  padding: 12px 16px 8px;
  flex-shrink: 0;
`;

const AppPhoneBody = styled.div<{ gradient: string }>`
  flex: 1;
  background: ${({ gradient }) => gradient};
  padding: 8px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const AppPhoneLine = styled.div<{ $bold?: boolean; $dim?: boolean }>`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 11px;
  font-weight: ${({ $bold }) => $bold ? '700' : '400'};
  color: ${({ $dim }) => $dim ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.85)'};
  white-space: pre;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AppPhoneHomeBar = styled.div`
  height: 5px;
  margin: 6px auto;
  width: 35%;
  background: rgba(255,255,255,0.18);
  border-radius: 3px;
  flex-shrink: 0;
`;

/* ─── Phone mockup styles ────────────────────────────────────────────────── */

const PhoneMockup = styled.div<{ large: boolean; gradient: string }>`
  background: ${({ gradient }) => gradient};
  border: ${({ large }) => large ? '3px' : '2px'} solid rgba(255,255,255,0.15);
  border-radius: ${({ large }) => large ? '36px' : '18px'};
  width: ${({ large }) => large ? '220px' : '80px'};
  height: ${({ large }) => large ? '440px' : '145px'};
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  box-shadow: 0 8px 32px rgba(0,0,0,0.4);
`;

const PhoneStatusBar = styled.div`
  height: 10px;
  background: rgba(0,0,0,0.2);
  flex-shrink: 0;
`;

const PhoneScreen = styled.div<{ small?: boolean }>`
  flex: 1;
  padding: ${({ small }) => small ? '6px' : '16px'};
  display: flex;
  flex-direction: column;
  gap: ${({ small }) => small ? '3px' : '8px'};
  overflow: hidden;
`;

const MockLine = styled.div<{ $bold?: boolean; $dim?: boolean; $small?: boolean }>`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: ${({ $small }) => $small ? '7px' : '13px'};
  font-weight: ${({ $bold }) => $bold ? '700' : '400'};
  color: ${({ $dim }) => $dim ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.9)'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PhoneHomeBar = styled.div`
  height: 6px;
  margin: 6px auto;
  width: 40%;
  background: rgba(255,255,255,0.2);
  border-radius: 3px;
  flex-shrink: 0;
`;

const ThumbnailWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  opacity: 0.85;
  transition: opacity 0.2s, transform 0.2s;
  &:hover {
    opacity: 1;
    transform: scale(1.03);
  }
`;

const ThumbnailHint = styled.span`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 10px;
  color: rgba(255,255,255,0.35);
`;

/* ─── Lightbox styles ────────────────────────────────────────────────────── */

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.15s ease;
`;

const LightboxInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  position: relative;
`;

const LightboxClose = styled.button`
  position: absolute;
  top: -48px;
  right: -8px;
  background: rgba(255,255,255,0.1);
  border: none;
  border-radius: 50%;
  color: #fff;
  width: 36px;
  height: 36px;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: rgba(255,255,255,0.2); }
`;

const LightboxLabel = styled.div`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 14px;
  color: rgba(255,255,255,0.5);
`;

/* ─── Report modal styles ────────────────────────────────────────────────── */

const ReportDetailsButton = styled.button`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #3aad5a;
  background: transparent;
  border: 1px solid rgba(58, 173, 90, 0.35);
  border-radius: 100px;
  padding: 5px 14px;
  margin-top: 10px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: rgba(58, 173, 90, 0.12);
  }
`;

const ReportModalInner = styled.div`
  background: #122018;
  border: 1px solid rgba(58, 173, 90, 0.2);
  border-radius: 16px;
  padding: 32px;
  max-width: 560px;
  width: calc(100vw - 48px);
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
`;

const ReportModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const ReportModalTitle = styled.h3`
  font-family: 'Montserrat', system-ui, sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
`;

const ReportModalDescText = styled.p`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 14px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 20px;
`;

const ReportCloseButton = styled.button`
  background: rgba(255,255,255,0.08);
  border: none;
  border-radius: 50%;
  color: rgba(255,255,255,0.7);
  width: 32px;
  height: 32px;
  font-size: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &:hover { background: rgba(255,255,255,0.16); color: #fff; }
`;

const ReportSections = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;

const ReportSection = styled.div``;

const ReportSectionTitle = styled.div`
  font-family: 'Montserrat', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #3aad5a;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const ReportList = styled.ul`
  margin: 0;
  padding: 0 0 0 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReportListItem = styled.li`
  font-family: 'Hind', system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.65);
`;

export default Puttwise;
